import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Heart, LogOut, Menu, Plus, ShieldCheck, TrendingUp, User as UserIcon } from "lucide-react";

export const LOGO_WIDE = "https://customer-assets-39nsmqrw.emergentagent.net/job_home-finder-606/artifacts/bxxfjzh4_image.png";
export const LOGO_ROUND = "https://customer-assets-39nsmqrw.emergentagent.net/job_home-finder-606/artifacts/xzl6xhtg_image.png";

const NAV = [
  { label: "Rent", to: "/properties?mode=rent", tid: "nav-rent-link" },
  { label: "Buy", to: "/properties?mode=buy", tid: "nav-buy-link" },
  { label: "Sale", to: "/properties?mode=sale", tid: "nav-sale-link" },
  { label: "Market Watch", to: "/market-watch", tid: "nav-market-watch-link" },
];

export default function Header() {
  const { user, logout, openAuth } = useAuth();
  const [savedCount, setSavedCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return setSavedCount(0);
    api.get("/saved").then((r) => setSavedCount(r.data.length)).catch(() => {});
  }, [user]);

  const links = (onClick) => (
    <>
      {NAV.map((n) => (
        <Link key={n.tid} data-testid={n.tid} to={n.to} onClick={onClick}
          className="text-sm font-medium text-slate-700 transition-colors hover:text-blue-600">
          {n.label}
        </Link>
      ))}
      {user?.role === "admin" && (
        <Link data-testid="admin-panel-nav-link" to="/admin" onClick={onClick}
          className="flex items-center gap-1 text-sm font-medium text-violet-700 hover:text-violet-900">
          <ShieldCheck className="h-4 w-4" /> Admin
        </Link>
      )}
    </>
  );

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/85 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Sheet>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="rounded-lg p-2 hover:bg-secondary md:hidden">
              <Menu className="h-5 w-5" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72">
            <div className="mt-8 flex flex-col gap-5">{links()}</div>
          </SheetContent>
        </Sheet>

        <Link to="/" data-testid="brand-logo-link" className="flex items-center gap-2">
          <img src={LOGO_WIDE} alt="Rent-ya-Buy.com" className="h-14 w-auto object-contain mix-blend-multiply" />
          <div className="hidden leading-tight sm:block">
            <span className="font-heading text-lg font-extrabold tracking-tight">Rent-ya-Buy</span>
            <span className="block text-[10px] font-medium text-muted-foreground">Rent It. Buy It. Own It.</span>
          </div>
        </Link>

        <nav className="ml-6 hidden items-center gap-6 md:flex">{links()}</nav>

        <div className="ml-auto flex items-center gap-2">
          <button
            data-testid="saved-nav-button"
            onClick={() => (user ? navigate("/saved") : openAuth())}
            className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-secondary"
            title="Favorites & Shortlist"
          >
            <Heart className="h-5 w-5" />
            {savedCount > 0 && (
              <span data-testid="saved-count-badge" className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-600 text-[10px] font-bold text-white">
                {savedCount}
              </span>
            )}
          </button>
          <Button
            data-testid="post-property-nav-button"
            size="sm"
            className="hidden gap-1 bg-emerald-600 text-white hover:bg-emerald-700 sm:flex"
            onClick={() => (user ? navigate("/post-property") : openAuth())}
          >
            <Plus className="h-4 w-4" /> Post Property
          </Button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium hover:bg-secondary">
                  <UserIcon className="h-4 w-4" />
                  <span className="hidden max-w-[120px] truncate sm:inline">{user.name || user.email}</span>
                  {user.is_member && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">MEMBER</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="menu-saved" onClick={() => navigate("/saved")}>
                  <Heart className="mr-2 h-4 w-4" /> Favorites & Cart
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-market-watch" onClick={() => navigate("/market-watch")}>
                  <TrendingUp className="mr-2 h-4 w-4" /> Market Watch
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-logout" onClick={logout}>
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="signin-button" size="sm" variant="outline" onClick={openAuth}>
              Sign In
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
